import type { QtFilterItem, QtFilterLogic, QtFilterModel } from '@/components/table/filterModel';
import type { GridViewState } from './gridViewState';
import { jsonEqual } from './json';

/**
 * Merges which rows a grid opens with (filter and sort) with the tenant's default for it.
 *
 * A three-way merge per field: the user's stored view, the default it was derived from (`base`),
 * and the default now in effect (`target`). A field the user left as `base` gave it follows
 * `target`; one the user changed stays theirs. Both defaults fall back to the screen's first-visit
 * view when the tenant has none, so an entry stored before tenant defaults existed merges against
 * what it was really derived from.
 *
 * Dependency-free for the same reason as `gridViewState`: the Knockout host runs it.
 */

/** A default's side of the view. The search text is never part of a default. */
export interface GridViewDefault {
  filter: string;
  sort: string[];
}

export function mergeGridView(
  stored: GridViewState | null,
  base: GridViewDefault,
  target: GridViewDefault
): GridViewState {
  if (stored === null) {
    return { search: '', filter: target.filter, sort: target.sort };
  }

  return {
    search: stored.search,
    filter: sameFilter(stored.filter, base.filter) ? target.filter : stored.filter,
    sort: jsonEqual(stored.sort, base.sort) ? target.sort : stored.sort,
  };
}

/** The first-visit view, as a default. */
export function firstVisitGridView(firstVisit: Partial<GridViewState>): GridViewDefault {
  return { filter: firstVisit.filter ?? '', sort: firstVisit.sort ?? [] };
}

/**
 * The view part of a stored table-state snapshot, in the vocabulary the host keeps views in: the
 * filter as the endpoint's JSON, the sort as `-field` keys. `null` for anything that is not a
 * snapshot object. Read defensively: the snapshot is opaque JSON to the host.
 */
export function gridViewDefaultFrom(snapshot: unknown): GridViewDefault | null {
  if (typeof snapshot !== 'object' || snapshot === null || Array.isArray(snapshot)) {
    return null;
  }

  const { filterModel, sorting } = snapshot as { filterModel?: unknown; sorting?: unknown };
  const items = filterItemsOf(filterModel);
  const sort = Array.isArray(sorting)
    ? sorting.flatMap((entry: unknown) => {
        const { id, desc } = (entry ?? {}) as { id?: unknown; desc?: unknown };
        return typeof id === 'string' ? [`${desc === true ? '-' : ''}${id}`] : [];
      })
    : [];

  return { filter: items.length > 0 ? JSON.stringify(filterModel) : '', sort };
}

/** A filter as far as which rows it selects. */
interface ComparableFilter {
  logic: QtFilterLogic;
  items: Omit<QtFilterItem, 'id'>[];
}

/** Whether two filters select the same rows, whatever the random id of each condition. */
export function sameFilter(left: string, right: string): boolean {
  return jsonEqual(comparableFilter(left), comparableFilter(right));
}

/**
 * `filter` without what does not change its meaning: the ids of its conditions, and the
 * difference between no filter and one with no conditions. An unreadable filter is compared as
 * it is.
 */
function comparableFilter(filter: string): ComparableFilter | string | null {
  if (filter === '') {
    return null;
  }

  let model: unknown;
  try {
    model = JSON.parse(filter);
  } catch {
    return filter;
  }

  const items = filterItemsOf(model);
  if (items.length === 0) {
    return null;
  }

  // The shape the table writes; nothing has validated it beyond `filterItemsOf`.
  const { logic } = model as Partial<QtFilterModel>;
  return {
    logic: logic ?? 'and',
    items: (items as QtFilterItem[]).map(({ id: _id, ...condition }) => condition),
  };
}

function filterItemsOf(model: unknown): unknown[] {
  if (typeof model !== 'object' || model === null) {
    return [];
  }

  const { items } = model as { items?: unknown };
  return Array.isArray(items) ? items : [];
}
